import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { buildAmenityOptions, buildAmenityMarkerGroups } from './amenities';

const DEFAULT_AMENITY_VISIBILITY = {
  grocery: true,
  gyms: true,
  favorites: true,
  hotSprings: false,
  skiResorts: false,
};

export function useAmenityControls({
  stores = [],
  gyms = [],
  hotSpringCount = 0,
  skiResortCount = 0,
  showThingsToDo,
  showStoreMarkers,
  showGymMarkers,
}) {
  const [amenityVisibility, setAmenityVisibility] = useState(DEFAULT_AMENITY_VISIBILITY);
  const prevHotCountRef = useRef(0);
  const prevSkiCountRef = useRef(0);

  useEffect(() => {
    if (!showThingsToDo) return;
    if (hotSpringCount > 0 && prevHotCountRef.current === 0) {
      setAmenityVisibility((prev) => (prev.hotSprings ? prev : { ...prev, hotSprings: true }));
      prevHotCountRef.current = hotSpringCount;
    }
    if (hotSpringCount === 0) prevHotCountRef.current = 0;
  }, [showThingsToDo, hotSpringCount]);

  useEffect(() => {
    if (!showThingsToDo) return;
    if (skiResortCount > 0 && prevSkiCountRef.current === 0) {
      setAmenityVisibility((prev) => (prev.skiResorts ? prev : { ...prev, skiResorts: true }));
      prevSkiCountRef.current = skiResortCount;
    }
    if (skiResortCount === 0) prevSkiCountRef.current = 0;
  }, [showThingsToDo, skiResortCount]);

  const handleAmenityToggle = useCallback((key) => {
    setAmenityVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const favoriteStores = useMemo(() => stores.filter((store) => store.isFavorite), [stores]);
  const groceryStores = useMemo(() => stores.filter((store) => !store.isFavorite), [stores]);

  const amenityOptions = useMemo(() => buildAmenityOptions({
    groceryCount: groceryStores.length,
    gymCount: gyms.length,
    favoriteCount: favoriteStores.length,
    hotSpringCount,
    skiResortCount,
    showThingsToDo,
  }), [groceryStores.length, gyms.length, favoriteStores.length, hotSpringCount, skiResortCount, showThingsToDo]);

  const amenityMarkerGroups = useMemo(() => buildAmenityMarkerGroups({
    showStoreMarkers,
    showGymMarkers,
    amenityVisibility,
    groceryStores,
    favoriteStores,
    gyms,
  }), [showStoreMarkers, showGymMarkers, amenityVisibility, groceryStores, favoriteStores, gyms]);

  return {
    amenityVisibility,
    handleAmenityToggle,
    amenityOptions,
    amenityMarkerGroups,
  };
}
